<template>
  <b-col
    class="thumb-col mb-3"
    qa="thumbnail-card"
    :class="colClass"
  >
    <div
      v-if="!videoThumbnail"
      class="thumb-card"
      :class="{ 'associated' : associated }"
    >
      <b-container>
        <b-row>
          <b-col
            lg="4"
            xl="4"
            sm="3"
            class="p-2"
          >
            <img
              v-tooltip="associated ? $t('global.description.associatedPicture') : false"
              :src="getThumbnailImage"
              class="img-fluid"
              :class="{ 'selectable' : selectable }"
              aria-hidden="true"
              alt="associated-picture"
              @click="onSelected()"
            >
          </b-col>

          <b-col
            lg="8"
            xl="8"
            sm="9"
            class="pl-1 thumb-text mb-1 p-1"
          >
            <div class="mt-1">
              <a
                v-if="selectable"
                type="button"
                class="linkbtn"
                @click="onSelected()"
              >
                <h6
                  class="photo-text-truncated"
                  qa="thumbnail-file-name"
                >{{ attachment.filename }}</h6>
              </a>
              <h6
                v-else
                class="photo-text-truncated"
              >
                {{ attachment.filename }}
              </h6>
            </div>
            <div class="mt-1 thumb-author">
              {{ attachment.createdBy.fullName }}
            </div>
            <div class="thumb-date mt-2">
              {{ attachment.creationDate | datetime }}
            </div>
            <div class="thumb-author">
              {{ fileSize }}
            </div>
          </b-col>

          <slot>
            <span
              v-if="checkable"
              class="thumb-check"
            >
              <b-form-checkbox
                v-if="visibleCheckbox"
                ref="checkbox"
                v-model="attachment.checked"
                qa="thumbnail-checkbox"
                name="checkbox"
                :value="true"
                :unchecked-value="false"
                @change="onChecked(attachment)"
              />
            </span>
          </slot>

          <div
            v-if="downloadable"
            class="download-icon"
          >
            <vt-loading-button
              v-if="visibleDownloadButton"
              v-tooltip="$t('grossing.content.downloadPhoto')"
              qa="thumbnail-download-button"
              class="btn__icon btn__icon--primary mx-1 p-0"
              :params="{ promise: downloadImage }"
            >
              <i
                class="fal fa-arrow-to-bottom"
                aria-hidden="true"
              />
            </vt-loading-button>
          </div>
        </b-row>
      </b-container>
    </div>
    <div
      v-else
      class="thumb-card"
    >
      <div class="video-layer">
        <img
          v-if="thumbVisible"
          :src="getThumbnailImage"
          class="video-poster"
          :class="{ 'selectable' : selectable }"
          aria-hidden="true"
          alt="video-poster"
          @click="onSelected()"
        >
        <b-button
          v-if="thumbVisible"
          qa="thumbnail-play-video-button"
          class="play-btn"
          @click="playVideo()"
        >
          <i
            class="far fa-play-circle"
            aria-hidden="true"
          />
        </b-button>
        <video
          v-if="!thumbVisible"
          ref="videoThumb"
          :src="getUrlFile()"
          :controls="controls"
          class="videoplayer"
          autoplay
        />
      </div>

      <div class="p-2 thumb-text video">
        <div>
          <a
            v-if="selectable"
            type="button"
            class="linkbtn"
            target="_blank"
          >
            <h6 class="video-text-truncated">{{ attachment.filename }}</h6>
          </a>
          <h6
            v-else
            class="video-text-truncated"
          >
            {{ attachment.filename }}
          </h6>
        </div>
        <div class="mt-1 thumb-author">
          {{ attachment.createdBy.fullName }}
        </div>
        <div class="thumb-date mt-2">
          {{ attachment.creationDate | datetime }}
        </div>
        <div class="thumb-author">
          {{ fileSize }}
        </div>
      </div>

      <slot>
        <span
          v-if="checkable"
          class="thumb-check"
        >
          <b-form-checkbox
            ref="checkbox"
            v-model="attachment.checked"
            qa="thumbnail-checkbox"
            name="checkbox"
            :value="true"
            :unchecked-value="false"
            @change="onChecked(attachment)"
          />
        </span>
      </slot>

      <div
        v-if="downloadable"
        class="download-icon"
      >
        <b-button
          v-tooltip="$t('grossing.content.downloadPhoto')"
          qa="thumbnail-download-button"
          class="btn__icon btn__icon--primary mx-2 p-0"
          @click="downloadImage()"
        >
          <i
            class="fal fa-arrow-to-bottom"
            aria-hidden="true"
          />
        </b-button>
      </div>
    </div>
  </b-col>
</template>

<script>
import incidentAttachmentService from '@/api/resources/attachment/incidentAttachmentService'
import patientAttachmentService from '@/api/resources/attachment/patientAttachmentService'
import requestAttachmentService from '@/api/resources/attachment/requestAttachmentService'
import caseAttachmentService from '@/api/resources/attachment/caseAttachmentService'
import sampleAttachmentService from '@/api/resources/attachment/sampleAttachmentService'
import portionAttachmentService from '@/api/resources/attachment/portionAttachmentService'
import subportionAttachmentService from '@/api/resources/attachment/subportionAttachmentService'
import { saveAs } from 'file-saver'
import complementaryAttachmentService from '@/api/resources/attachment/complementaryAttachmentService'
import { ATTACHMENT_MIME_TYPES } from '@/api/resources/attachment/attachmentTypeService'

export default {
    name: 'VtThumbnail',
    props: {
        colClass: { type: String, default: 'col-lg-4 col-xl-3 col-md-6 col-12' },
        selectable: { type: Boolean, default: true },
        checkable: { type: Boolean, default: true },
        downloadable: { type: Boolean, default: true },
        attachment: {
            type: Object, /* required: true, */
            default: () => {
                return {
                    creationDate: '17-04-2018 12:50',
                    filename: 'NombreArchivo.jpg',
                    createdBy: {
                        name: 'Juan',
                        surname1: 'Sánchez'
                    },
                    mimetype: 'image/png',
                    length: 2694840
                }
            }
        },
        laboratoryId: { type: Number },
        requestId: { type: Number },
        caseId: { type: Number },
        sampleId: { type: Number },
        complementaryId: { type: Number },
        portionId: { type: Number },
        subportionId: { type: Number },
        patientId: { type: Number },
        incidentId: { type: Number },
        originalImage: { type: Boolean },
        sampleLabel: { type: String }
    },
    data() {
        return {
            isPlaying: false,
            controls: true,
            thumbVisible: true
        }
    },
    computed: {
        visibleDownloadButton() {
            return !this.isUriList
        },
        visibleCheckbox() {
            return !this.isUriList
        },
        isUriList() {
            return this.attachment.mimetype === ATTACHMENT_MIME_TYPES.URI
        },
        associated() {
            return this.attachment.associated
        },
        videoThumbnail() {
            return this.attachment.mimetype.startsWith(ATTACHMENT_MIME_TYPES.VIDEO)
        },
        fileSize() {
            if (this.attachment.length < 1024) {
                return this.attachment.length + ' Bytes'
            } else if (this.attachment.length < 1048576) {
                return Math.round(this.attachment.length / 1024) + ' KB'
            } else {
                return Math.round(this.attachment.length / 1048576) + ' MB'
            }
        },
        isSelected() {
            return !!this.$refs.checkbox?.computedLocalChecked
        },
        viewerData() {
            if (this.subportionId) {
                return {
                    mimeType: this.attachment.mimetype,
                    laboratoryId: this.laboratoryId,
                    caseId: this.caseId,
                    sampleId: this.sampleId,
                    portionId: this.portionId,
                    subportionId: this.subportionId,
                    attachmentId: this.attachment.attachmentId,
                    label: this.sampleLabel,
                    url: this.getUrlFile(),
                    attachmentEntityType: this.attachment.attachmentEntityType
                }
            } else if (this.sampleId) {
                return {
                    mimeType: this.attachment.mimetype,
                    laboratoryId: this.laboratoryId,
                    caseId: this.caseId,
                    sampleId: this.sampleId,
                    portionId: this.portionId,
                    subportionId: this.subportionId,
                    attachmentId: this.attachment.attachmentId,
                    label: this.sampleLabel,
                    url: this.getUrlFile(),
                    attachmentEntityType: this.attachment.attachmentEntityType
                }
            } else {
                return {
                    mimeType: 'OTHER',
                    url: this.getUrlFile()
                }
            }
        },
        getThumbnailImage() {
            if (this.attachment.thumbnailBytes) {
                if (this.attachment.thumbnailBytes.startsWith('data:')) {
                    return this.attachment.thumbnailBytes
                } else {
                    return `data:${this.attachment.mimetype};base64, ${this.attachment.thumbnailBytes}`
                }
            }
            return ''
        }
    },
    methods: {
        async playVideo() {
            this.thumbVisible = !this.thumbVisible
        },
        net(value) {
            if (value) {
                return value
            }
            return ''
        },
        async getImage() {
            let [err, blob] = []
            if (this.subportionId) {
                [err, blob] = await this.toCall(subportionAttachmentService.getFile(
                    this.laboratoryId, this.caseId, this.sampleId, this.portionId, this.subportionId, this.attachment.attachmentId))
            } else if (this.portionId) {
                [err, blob] = await this.toCall(portionAttachmentService.getFile(
                    this.laboratoryId, this.caseId, this.sampleId, this.portionId, this.attachment.attachmentId))
            } else if (this.sampleId) {
                [err, blob] = await this.toCall(sampleAttachmentService.getFile(
                    this.laboratoryId, this.caseId, this.sampleId, this.attachment.attachmentId))
            } else if (this.complementaryId) {
                [err, blob] = await this.toCall(complementaryAttachmentService.getFile(
                    this.laboratoryId, this.caseId, this.complementaryId, this.attachment.attachmentId))
            } else if (this.caseId) {
                [err, blob] = await this.toCall(caseAttachmentService.getFile(
                    this.laboratoryId, this.caseId, this.attachment.attachmentId))
            } else if (this.requestId) {
                [err, blob] = await this.toCall(requestAttachmentService.getFile(
                    this.laboratoryId, this.requestId, this.attachment.attachmentId))
            } else if (this.incidentId) {
                [err, blob] = await this.toCall(incidentAttachmentService.getFile(
                    this.laboratoryId, this.incidentId, this.attachment.attachmentId))
            } else if (this.patientId) {
                [err, blob] = await this.toCall(patientAttachmentService.getFile(
                    this.patientId, this.attachment.attachmentId))
            }
            return [err, blob]
        },
        getUrlFile() {
            let urlFile
            if (this.subportionId) {
                urlFile = subportionAttachmentService.getUrlFile(this.laboratoryId, this.caseId, this.sampleId, this.portionId, this.subportionId, this.attachment.attachmentId)
            } else if (this.portionId) {
                urlFile = portionAttachmentService.getUrlFile(this.laboratoryId, this.caseId, this.sampleId, this.portionId, this.attachment.attachmentId)
            } else if (this.sampleId) {
                urlFile = sampleAttachmentService.getUrlFile(this.laboratoryId, this.caseId, this.sampleId, this.attachment.attachmentId)
            } else if (this.complementaryId) {
                urlFile = complementaryAttachmentService.getUrlForAttachment(this.laboratoryId, this.caseId, this.complementaryId, this.attachment.attachmentId)
            } else if (this.caseId) {
                urlFile = caseAttachmentService.getUrlFile(this.laboratoryId, this.caseId, this.attachment.attachmentId)
            } else if (this.requestId) {
                urlFile = requestAttachmentService.getUrlFile(this.laboratoryId, this.requestId, this.attachment.attachmentId)
            } else if (this.incidentId) {
                urlFile = incidentAttachmentService.getUrlFile(this.laboratoryId, this.incidentId, this.attachment.attachmentId)
            } else if (this.patientId) {
                urlFile = patientAttachmentService.getUrlFile(this.patientId, this.attachment.attachmentId)
            }
            return urlFile
        },
        async downloadImage() {
            let [err, blob] = await this.getImage()

            if (err || !blob) {
                this.showErrorModalGlobal(this.$t('component.attachModal.error.thumbnailDownloadError'))
            } else {
                saveAs(blob, this.attachment.filename)
            }
        },
        deselect() {
            if (this.$refs.checkbox) {
                this.$refs.checkbox.localChecked = false
            }
        },
        onSelected() {
            if (this.selectable) {
                this.$emit('attachmentSelected', this.viewerData)
            }
        },
        onChecked(attachment) {
            this.$emit('attachmentChecked', attachment)
        }
    }
}
</script>

<style lang="scss" scoped>
  .selectable {
    cursor: pointer;
  }

  .play-btn{
    position:absolute;
    left:50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size:35px;
    background: transparent !important;
    border: transparent !important;
  }

  .play-btn i{
    color: #FFF;
    transition: .3s;
  }

  .play-btn:hover i{
    color: vt-color(primary);
  }

  .video-poster{
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .thumb-text.video{
    padding-top: 0 !important;
  }

  .video-layer{
    position: relative;
    height: 140px;
    background: rgb(0, 0, 0);
    margin-bottom: 5px;
    border-radius: 5px 5px 0 0;
  }

  .video-controls{
    position: absolute;
    bottom: 5px;
    background: vt-color(dark-text, .5);
    width: 100%;
  }

  .video-controls .btn{
    color: #FFF !important;
    transition: .3s;
    font-size: font-size(small);
  }

  .video-controls .btn:hover{
    color: vt-color(primary) !important;
  }

  .videoplayer{
    width: 100%;
    height: 100%;
    border-radius: 5px 5px 0 0;
  }

  .thumb-header {
    border: 1px solid #d8d8d8;
  }

  .thumb-content {
    max-height: 180px;
    overflow: hidden;
    overflow-y: auto;
    padding-right: 10px;
    padding-bottom: 20px;
  }

  .thumb-img{
    max-height: 55px;
  }

  .video-text-truncated{
    width: 100% !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    line-height: 13px !important;
    margin: 0 !important;
  }

  .photo-text-truncated{
    width: calc(100% - 20px) !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    line-height: 13px !important;
    margin: 0 !important;
  }

  .linkbtn h6 {
    line-height: 13px;
    margin: 0;
  }

  .thumb-card {
    position: relative;
  }

  .thumb-card:active {
    box-shadow: 0 2px 5px 0 #9c9c9c;
  }

  .thumb-check {
    position: absolute;
    right: 2px;
    top: 2px;
  }

  .thumb-card h6 {
    font-size: 1rem;
    color: vt-color(primary);
    line-height: .5;
    margin-top: 10px;
  }

  .thumb-author {
    font-size: .9rem;
  }

  .thumb-date {
    font-size: .9rem;
  }

  .thumb-text {
    line-height: 14px;
  }

  .download-icon {
    position: absolute;
    right: 5px;
    bottom: 1px;
  }

  @media (max-width: 1370px) {

    .video-layer{
      height: 110px;
    }

    .thumb-card h6 {
      font-size: .85rem;
      color: vt-color(primary);
      line-height: .5;
      margin-top: 8px;
    }

    .thumb-author {
      font-size: .7rem;
    }

    .thumb-date {
      font-size: .7rem;
    }

    .thumb-text {
      line-height: 9px;
    }

  }

  @media (min-width: 1400px) {

    .linkbtn h6 {
      width: 140px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin: 0 !important;
    }

    .thumb-card h6 {
      font-size: .9rem;
      color: vt-color(primary);
      line-height: 23px !important;
      margin-top: 8px;
    }

    .thumb-author {
      font-size: .7rem;
    }

    .thumb-date {
      font-size: .7rem;
    }

    .thumb-text {
      line-height: 9px;
    }

  }
</style>
